/**
 * Enhanced React Error Boundary Component for Production
 *
 * Provides comprehensive error handling with:
 * - HTTP status code specific handling (400, 401, 403, 404, 422, 500+)
 * - User-friendly error messages with actionable guidance
 * - Error recovery mechanisms (retry, navigation, session recovery)
 * - Integration with notification system and authentication
 * - Network connectivity error handling
 * - Permission-based error messages with role context
 * - Validation error handling with field-specific guidance
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Collapse, IconButton, Chip } from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  BugReport,
  Home,
  Login,
  Wifi,
  Security,
  Warning,
  ContactSupport,
  NavigateBefore
} from '@mui/icons-material';
import { errorHandler, EnhancedApiError } from '../../services/errorHandling';
import type { NotificationOptions } from './NotificationSystem';

/**
 * HTTP Status Code specific error information
 */
interface HttpErrorInfo {
  status: number;
  title: string;
  message: string;
  icon: ReactNode;
  severity: 'error' | 'warning' | 'info';
  recoveryActions: Array<{
    label: string;
    action: 'retry' | 'navigate' | 'login' | 'contact' | 'reload' | 'back';
    path?: string;
    primary?: boolean;
  }>;
}

/**
 * Enhanced Props interface with production error handling capabilities
 */
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  level?: 'page' | 'section' | 'component';
  showNotification?: (options: NotificationOptions) => string;
  enableGlobalToasts?: boolean;
  // New production error handling props
  enableHttpErrorHandling?: boolean;
  enableAuthRedirect?: boolean;
  enableNetworkRetry?: boolean;
  maxRetryAttempts?: number;
  retryDelay?: number;
  customErrorMessages?: Record<number, string>;
  onAuthError?: () => void;
  onNetworkError?: () => void;
  onPermissionError?: (userRole?: string) => void;
}

/**
 * Enhanced State interface with comprehensive error context
 */
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  errorId: string;
  // Enhanced error state
  httpErrorInfo: HttpErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  networkStatus: 'online' | 'offline' | 'checking';
  lastRetryTime: number | null;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private networkCheckInterval: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: '',
      // Enhanced error state
      httpErrorInfo: null,
      retryCount: 0,
      isRetrying: false,
      networkStatus: 'online',
      lastRetryTime: null,
    };
  }

  componentWillUnmount() {
    // Clean up timers
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    if (this.networkCheckInterval) {
      clearInterval(this.networkCheckInterval);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `boundary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Enhanced error analysis for HTTP errors
    let httpErrorInfo: HttpErrorInfo | null = null;
    
    // Check if this is an enhanced API error with HTTP status
    if (error instanceof EnhancedApiError) {
      httpErrorInfo = ErrorBoundary.getHttpErrorInfo(error.status, error.code, error.userMessage);
    } else if (error.message) {
      // Try to extract HTTP status from error message
      const statusMatch = error.message.match(/(\d{3})/);
      if (statusMatch) {
        const status = parseInt(statusMatch[1], 10);
        if (status >= 400) {
          httpErrorInfo = ErrorBoundary.getHttpErrorInfo(status, 'GENERIC_ERROR', error.message);
        }
      }
    }
    
    return {
      hasError: true,
      error,
      errorId,
      httpErrorInfo,
      retryCount: 0,
      isRetrying: false,
      networkStatus: navigator.onLine ? 'online' : 'offline',
      lastRetryTime: null,
    };
  }

  /**
   * Get comprehensive HTTP error information with user-friendly messages and recovery actions
   */
  private static getHttpErrorInfo(status: number, code: string, originalMessage?: string): HttpErrorInfo {
    switch (status) {
      case 400:
        return {
          status,
          title: 'Invalid Request',
          message: originalMessage || 'The request contains invalid data. Please check your input and try again.',
          icon: <Warning color="error" />,
          severity: 'error',
          recoveryActions: [
            { label: 'Check Input', action: 'retry', primary: true },
            { label: 'Go Back', action: 'back' },
            { label: 'Contact Support', action: 'contact' },
          ],
        };

      case 401:
        return {
          status,
          title: 'Authentication Required',
          message: 'Your session has expired or you need to log in to access this resource.',
          icon: <Login color="error" />,
          severity: 'error',
          recoveryActions: [
            { label: 'Log In', action: 'login', primary: true },
            { label: 'Go Home', action: 'navigate', path: '/dashboard' },
          ],
        };

      case 403:
        return {
          status,
          title: 'Access Denied',
          message: 'You don\'t have permission to access this resource. Contact your administrator if you believe this is an error.',
          icon: <Security color="error" />,
          severity: 'error',
          recoveryActions: [
            { label: 'Go Back', action: 'back', primary: true },
            { label: 'Go Home', action: 'navigate', path: '/dashboard' },
            { label: 'Contact Support', action: 'contact' },
          ],
        };

      case 404:
        return {
          status,
          title: 'Resource Not Found',
          message: 'The requested resource could not be found. It may have been moved, deleted, or you may not have access to it.',
          icon: <Warning color="warning" />,
          severity: 'warning',
          recoveryActions: [
            { label: 'Go Back', action: 'back', primary: true },
            { label: 'Go Home', action: 'navigate', path: '/dashboard' },
            { label: 'Refresh Page', action: 'reload' },
          ],
        };

      case 422:
        return {
          status,
          title: 'Validation Error',
          message: 'The submitted data failed validation. Please check the highlighted fields and correct any errors.',
          icon: <Warning color="warning" />,
          severity: 'warning',
          recoveryActions: [
            { label: 'Fix Errors', action: 'retry', primary: true },
            { label: 'Reset Form', action: 'back' },
            { label: 'Get Help', action: 'contact' },
          ],
        };

      case 429:
        return {
          status,
          title: 'Too Many Requests',
          message: 'You\'ve made too many requests. Please wait a moment before trying again.',
          icon: <Warning color="warning" />,
          severity: 'warning',
          recoveryActions: [
            { label: 'Try Again Later', action: 'retry', primary: true },
            { label: 'Go Back', action: 'back' },
          ],
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          status,
          title: 'Server Error',
          message: 'The server encountered an error and could not complete your request. This is usually temporary.',
          icon: <BugReport color="error" />,
          severity: 'error',
          recoveryActions: [
            { label: 'Try Again', action: 'retry', primary: true },
            { label: 'Reload Page', action: 'reload' },
            { label: 'Contact Support', action: 'contact' },
          ],
        };

      default:
        // Network or unknown errors
        if (status === 0 || code === 'NETWORK_ERROR' || code === 'FETCH_ERROR') {
          return {
            status: 0,
            title: 'Connection Error',
            message: 'Unable to connect to the server. Please check your internet connection and try again.',
            icon: <Wifi color="error" />,
            severity: 'error',
            recoveryActions: [
              { label: 'Check Connection', action: 'retry', primary: true },
              { label: 'Reload Page', action: 'reload' },
              { label: 'Go Offline', action: 'back' },
            ],
          };
        }

        return {
          status,
          title: 'Unexpected Error',
          message: originalMessage || 'An unexpected error occurred. Please try again or contact support if the problem persists.',
          icon: <BugReport color="error" />,
          severity: 'error',
          recoveryActions: [
            { label: 'Try Again', action: 'retry', primary: true },
            { label: 'Reload Page', action: 'reload' },
            { label: 'Contact Support', action: 'contact' },
          ],
        };
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error using our enhanced error handler
    errorHandler.handleError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      level: this.props.level || 'component',
      timestamp: new Date().toISOString(),
    });

    this.setState({
      errorInfo,
    });

    // Handle specific error types with appropriate actions
    this.handleSpecificErrorTypes(error);

    // Show global toast notification for recoverable errors
    if (this.props.enableGlobalToasts && this.props.showNotification) {
      this.showErrorNotification();
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Start network monitoring if this is a network error
    if (this.isNetworkError(error)) {
      this.startNetworkMonitoring();
    }
  }

  /**
   * Handle specific error types with appropriate automated responses
   */
  private handleSpecificErrorTypes = (error: Error) => {
    const { enableAuthRedirect, enableNetworkRetry, onAuthError, onNetworkError, onPermissionError } = this.props;

    if (error instanceof EnhancedApiError) {
      switch (error.status) {
        case 401:
          // Authentication error - handle automatic redirect
          if (enableAuthRedirect) {
            this.handleAuthenticationError();
          }
          onAuthError?.();
          break;

        case 403:
          // Permission error - provide role context
          this.handlePermissionError();
          onPermissionError?.();
          break;

        case 0:
        case 500:
        case 502:
        case 503:
        case 504:
          // Network/server errors - handle retry logic
          if (enableNetworkRetry) {
            this.scheduleRetry();
          }
          onNetworkError?.();
          break;
      }
    } else if (this.isNetworkError(error)) {
      if (enableNetworkRetry) {
        this.scheduleRetry();
      }
      onNetworkError?.();
    }
  };

  /**
   * Show appropriate error notification based on error type
   */
  private showErrorNotification = () => {
    if (!this.props.showNotification) return;

    const level = this.props.level || 'component';
    const { httpErrorInfo } = this.state;

    if (httpErrorInfo) {
      // Show HTTP-specific error notification
      this.props.showNotification({
        severity: httpErrorInfo.severity,
        title: httpErrorInfo.title,
        message: httpErrorInfo.message,
        persistent: httpErrorInfo.severity === 'error',
        autoHideDuration: httpErrorInfo.severity === 'error' ? 15000 : 10000,
        actions: httpErrorInfo.recoveryActions.map(action => ({
          label: action.label,
          onClick: () => this.handleRecoveryAction(action.action, action.path),
          color: action.primary ? 'primary' : 'inherit',
        })),
      });
    } else {
      // Fallback to original notification logic
      if (level === 'component') {
        this.props.showNotification({
          severity: 'error',
          title: 'Component Error',
          message: 'A component encountered an error and has been reset. You can continue using the application.',
          persistent: true,
          autoHideDuration: 12000,
          actions: [
            {
              label: 'Retry',
              onClick: () => this.handleRetry(),
              color: 'inherit',
            },
          ],
        });
      } else if (level === 'section') {
        this.props.showNotification({
          severity: 'error',
          title: 'Section Error',
          message: 'A section of the application encountered an error. Some features may be temporarily unavailable.',
          persistent: true,
          autoHideDuration: 15000,
        });
      }
    }
  };

  /**
   * Handle authentication errors with automatic redirect
   */
  private handleAuthenticationError = () => {
    // Clear any existing auth tokens
    localStorage.removeItem('auth_token');
    
    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  };

  /**
   * Handle permission errors with role context
   */
  private handlePermissionError = () => {
    // Get current user role for context
    const userRole = this.getCurrentUserRole();
    
    // Update error message with role context
    if (this.state.httpErrorInfo) {
      this.setState({
        httpErrorInfo: {
          ...this.state.httpErrorInfo,
          message: `Access denied for ${userRole} role. ${this.state.httpErrorInfo.message}`,
        },
      });
    }
  };

  /**
   * Get current user role from auth store
   */
  private getCurrentUserRole = (): string => {
    try {
      // Access auth store state directly
      const authState = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      return authState.state?.user?.role || 'user';
    } catch {
      return 'user';
    }
  };

  /**
   * Check if error is network-related
   */
  private isNetworkError = (error: Error): boolean => {
    return (
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('connection') ||
      error.name === 'NetworkError' ||
      error.name === 'TypeError' && error.message.includes('Failed to fetch')
    );
  };

  /**
   * Schedule automatic retry with exponential backoff
   */
  private scheduleRetry = () => {
    const { maxRetryAttempts = 3, retryDelay = 1000 } = this.props;
    const { retryCount } = this.state;

    if (retryCount >= maxRetryAttempts) {
      return; // Max retries reached
    }

    const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff
    
    this.setState({ isRetrying: true });

    this.retryTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  /**
   * Start monitoring network connectivity
   */
  private startNetworkMonitoring = () => {
    // Monitor online/offline status
    const updateNetworkStatus = () => {
      this.setState({
        networkStatus: navigator.onLine ? 'online' : 'offline',
      });

      // Auto-retry when connection is restored
      if (navigator.onLine && this.state.networkStatus === 'offline') {
        setTimeout(() => {
          this.handleRetry();
        }, 1000);
      }
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Periodic connectivity check
    this.networkCheckInterval = setInterval(() => {
      this.setState({ networkStatus: 'checking' });
      
      // Simple connectivity test
      fetch('/api/health', { method: 'HEAD', cache: 'no-cache' })
        .then(() => {
          this.setState({ networkStatus: 'online' });
        })
        .catch(() => {
          this.setState({ networkStatus: 'offline' });
        });
    }, 30000); // Check every 30 seconds
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      httpErrorInfo: null,
      retryCount: this.state.retryCount + 1,
      isRetrying: false,
      lastRetryTime: Date.now(),
    });
  };

  /**
   * Handle recovery actions from error UI
   */
  handleRecoveryAction = (action: string, path?: string) => {
    switch (action) {
      case 'retry':
        this.handleRetry();
        break;
      
      case 'navigate':
        if (path) {
          window.location.href = path;
        }
        break;
      
      case 'login':
        window.location.href = '/login';
        break;
      
      case 'contact':
        // Open support contact - could be email, chat, or support page
        window.open('mailto:support@cedarheightsmusicacademy.com', '_blank');
        break;
      
      case 'reload':
        window.location.reload();
        break;
      
      case 'back':
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = '/dashboard';
        }
        break;
      
      default:
        console.warn(`Unknown recovery action: ${action}`);
    }
  };

  handleToggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  handleReportError = () => {
    const { error, errorInfo, errorId } = this.state;
    
    // In a real app, this would send to an error reporting service
    const errorReport = {
      errorId,
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    
    // TODO: Send to error reporting service (Sentry, LogRocket, etc.)
    alert(`Error reported with ID: ${errorId}`);
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails, errorId, httpErrorInfo, isRetrying, networkStatus, retryCount } = this.state;
      const { fallback, level = 'component', showDetails: showDetailsDefault = true, maxRetryAttempts = 3 } = this.props;

      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Use HTTP error info if available, otherwise fall back to generic error handling
      const errorTitle = httpErrorInfo?.title || (level === 'page' ? 'Application Error' : level === 'section' ? 'Section Error' : 'Component Error');
      const errorMessage = httpErrorInfo?.message || this.getGenericErrorMessage(level);
      const errorIcon = httpErrorInfo?.icon || <BugReport sx={{ color: 'error.main' }} />;
      const errorSeverity = httpErrorInfo?.severity || 'error';
      const recoveryActions = httpErrorInfo?.recoveryActions || this.getDefaultRecoveryActions(level);

      // Determine the severity and styling based on level and HTTP error info
      const isPageLevel = level === 'page';
      const isSectionLevel = level === 'section';
      const isError = errorSeverity === 'error';

      return (
        <Box
          sx={{
            p: isPageLevel ? 4 : isSectionLevel ? 3 : 2,
            m: isPageLevel ? 2 : 1,
            border: '1px solid',
            borderColor: isError ? 'error.main' : 'warning.main',
            borderRadius: 2,
            backgroundColor: isError ? 'error.light' : 'warning.light',
            color: isError ? 'error.contrastText' : 'warning.contrastText',
            minHeight: isPageLevel ? '50vh' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: isPageLevel ? 'center' : 'flex-start',
          }}
        >
          {/* Error Icon */}
          <Box sx={{ fontSize: isPageLevel ? 64 : isSectionLevel ? 48 : 32, mb: 2 }}>
            {errorIcon}
          </Box>
          
          {/* Error Title */}
          <Typography
            variant={isPageLevel ? 'h4' : isSectionLevel ? 'h5' : 'h6'}
            component="h2"
            gutterBottom
            align="center"
          >
            {errorTitle}
          </Typography>

          {/* HTTP Status Code Badge */}
          {httpErrorInfo && (
            <Chip
              label={`HTTP ${httpErrorInfo.status}`}
              color={isError ? 'error' : 'warning'}
              size="small"
              sx={{ mb: 2 }}
            />
          )}
          
          {/* Error Message */}
          <Typography
            variant="body1"
            align="center"
            sx={{ mb: 3, maxWidth: 600 }}
          >
            {errorMessage}
          </Typography>

          {/* Network Status Indicator */}
          {networkStatus !== 'online' && (
            <Alert severity="warning" sx={{ mb: 2, maxWidth: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Wifi />
                <Typography variant="body2">
                  {networkStatus === 'offline' ? 'You appear to be offline' : 'Checking connection...'}
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Retry Status */}
          {isRetrying && (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 600 }}>
              <Typography variant="body2">
                Retrying... (Attempt {retryCount + 1} of {maxRetryAttempts})
              </Typography>
            </Alert>
          )}

          {/* Recovery Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {recoveryActions.map((action, index) => (
              <Button
                key={index}
                variant={action.primary ? 'contained' : 'outlined'}
                color={action.primary ? 'primary' : 'secondary'}
                startIcon={this.getActionIcon(action.action)}
                onClick={() => this.handleRecoveryAction(action.action, action.path || undefined)}
                disabled={isRetrying}
              >
                {action.label}
              </Button>
            ))}
            
            {/* Report Issue Button */}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ContactSupport />}
              onClick={this.handleReportError}
            >
              Report Issue
            </Button>
          </Box>

          {/* Technical Details Section */}
          {showDetailsDefault && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={this.handleToggleDetails}
                endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                sx={{ mb: 1 }}
              >
                {showDetails ? 'Hide' : 'Show'} Technical Details
              </Button>

              <Collapse in={showDetails} sx={{ width: '100%' }}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Error ID: {errorId}
                  </Typography>
                  
                  {/* HTTP Error Details */}
                  {httpErrorInfo && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>HTTP Status:</strong> {httpErrorInfo.status}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Error Type:</strong> {httpErrorInfo.title}
                      </Typography>
                    </Box>
                  )}

                  {/* Error Message and Stack */}
                  <Typography variant="body2" gutterBottom>
                    <strong>Error:</strong> {error?.message}
                  </Typography>
                  
                  {/* Network Status */}
                  <Typography variant="body2" gutterBottom>
                    <strong>Network Status:</strong> {networkStatus}
                  </Typography>
                  
                  {/* Retry Information */}
                  <Typography variant="body2" gutterBottom>
                    <strong>Retry Count:</strong> {retryCount}
                  </Typography>

                  {error?.stack && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Stack Trace:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          p: 1,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: 200,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {error.stack}
                      </Typography>
                    </Box>
                  )}
                  {errorInfo?.componentStack && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Component Stack:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          p: 1,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: 200,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {errorInfo.componentStack}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              </Collapse>
            </>
          )}
        </Box>
      );
    }

    return this.props.children;
  }

  /**
   * Get generic error message based on level
   */
  private getGenericErrorMessage = (level: string): string => {
    switch (level) {
      case 'page':
        return 'We\'re sorry, but something went wrong with the application. Our team has been notified and is working to fix this issue.';
      case 'section':
        return 'This section encountered an error and couldn\'t load properly. You can try refreshing or continue using other parts of the application.';
      default:
        return 'This component encountered an error. You can try refreshing or the error may resolve itself.';
    }
  };

  /**
   * Get default recovery actions based on level
   */
  private getDefaultRecoveryActions = (level: string) => {
    const baseActions = [
      { label: 'Try Again', action: 'retry' as const, primary: true },
    ];

    if (level === 'page') {
      return [
        ...baseActions,
        { label: 'Reload Page', action: 'reload' as const },
        { label: 'Go Home', action: 'navigate' as const, path: '/dashboard' },
      ];
    }

    return [
      ...baseActions,
      { label: 'Go Back', action: 'back' as const },
    ];
  };

  /**
   * Get appropriate icon for recovery action
   */
  private getActionIcon = (action: string) => {
    switch (action) {
      case 'retry':
        return <Refresh />;
      case 'navigate':
      case 'back':
        return <NavigateBefore />;
      case 'login':
        return <Login />;
      case 'reload':
        return <Refresh />;
      case 'contact':
        return <ContactSupport />;
      default:
        return <Home />;
    }
  };
}

/**
 * Higher-order component to wrap components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

/**
 * Enhanced ErrorBoundary with global toast notifications
 */
export const ErrorBoundaryWithToasts: React.FC<Omit<Props, 'showNotification' | 'enableGlobalToasts'>> = (props) => {
  return (
    <ErrorBoundary
      {...props}
      enableGlobalToasts={true}
    />
  );
};

/**
 * Hook to manually trigger error boundary (for testing or error simulation)
 */
export function useErrorHandler() {
  return (error: Error) => {
    // This will be caught by the nearest error boundary
    throw error;
  };
}

export default ErrorBoundary;
